import { Component, DestroyRef, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartsInterface } from '../interfaces/charts';
import { DashBoardInterface, DashBoardTypeEnum } from '../interfaces/dashboard';
import { BehaviorSubject, EMPTY, Observable, Subject, catchError, filter, finalize, interval, map, merge, of, switchMap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { DashboardFocusService } from 'src/app/services/dashboard-focus.service';
import { GadGetAction } from '../interfaces/gadget-events';
import { DashboardViewService } from 'src/app/services/dashboard.services';
import { ChartCardConfigurationComponent } from './chart-card-configuration/chart-card-configuration.component';
import { AgChartOptions } from 'ag-charts-community';
import { DateUtils } from 'src/app/system-utils/utils/DateUtils';
import { ThemeService } from 'src/app/services/theme.service';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { buildChartTheme } from '../chart-theme';
import { openGadgetModal } from '../gadget-modal';

/*
 * Módulos NG-ZORRO usados no template: NzButtonModule, NzIconModule,
 * NzDropDownModule, NzToolTipModule (todos via SharedModule).
 *
 * SEPARAÇÃO runtime × persistido: item.options é o formato persistido
 * (Mktpdashboard.EXPRESSION). O componente renderiza chartOptions —
 * objeto DERIVADO com data das queries e theme dos tokens --tem-* —
 * e nunca escreve de volta em item.options.
 */
@Component({
    selector: 'chart-card',
    templateUrl: './chart-card.component.html',
    styleUrl: './chart-card.component.scss',
    standalone: false
})
export class ChartCardComponent implements OnInit, OnDestroy {

  lastUpdate = "01/01/1900 23:59:59";
  expand = false;
  focus = false;
  loading = false;
  tabName: string;

  /** options renderizadas (derivadas de item.options + data + theme) */
  chartOptions: AgChartOptions | null = null;

  @ViewChild("content", { static: true }) content: TemplateRef<any>;
  @Input() events: Observable<FormGroup | null>;
  @Input() dashboard: DashBoardInterface;
  @Input() name: string;
  @Input() item: ChartsInterface;
  @Output() deleting: EventEmitter<GadGetAction> = new EventEmitter<GadGetAction>();
  @Output() itemChange: EventEmitter<ChartsInterface> = new EventEmitter<ChartsInterface>();
  @Input() showConfiguration: Boolean = false;

  formFilter: FormGroup;
  overlayRef: OverlayRef | null = null;

  private readonly manualReload$ = new Subject<void>();
  private readonly refreshSeconds$ = new BehaviorSubject<number | null>(null);
  private lastRows: unknown[] | null = null;

  constructor(private dvs: DashboardViewService,
    private modal: NzModalService,
    public ThemeService: ThemeService, private overlay: Overlay,
    private vcr: ViewContainerRef,
    public dfs: DashboardFocusService,
    private message: NzMessageService,
    private el: ElementRef<HTMLElement>,
    private destroyRef: DestroyRef
  ) { }

  ngOnInit(): void {

    this.tabName = this.dashboard?.tabName;
    this.refreshSeconds$.next(this.item?.refresh ?? null);

    this.dfs.focusChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(c => this.focus = c);

    // re-tematiza o gráfico no toggle light/dark (sem tocar no persistido)
    this.ThemeService.isDarkTheme
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rebuildChartOptions());

    const filterTrigger$ = (this.events ?? EMPTY).pipe(
      map(form => {
        if (form != null) {
          this.formFilter = form;
        }
        return void 0;
      })
    );

    const refreshTick$ = this.refreshSeconds$.pipe(
      switchMap(seconds => (seconds != null && seconds > 0) ? interval(seconds * 1000) : EMPTY),
      filter(() => this.focus && !this.loading),
      map(() => void 0)
    );

    merge(filterTrigger$, this.manualReload$, refreshTick$)
      .pipe(
        switchMap(() => this.fetchRows$()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(rows => {
        if (rows != null) {
          this.lastUpdate = DateUtils.getNowFormatted("DD/MM/YYYY HH:mm:ss.SSS");
          this.lastRows = rows;
        }
        this.rebuildChartOptions();
        this.itemChange.emit(this.item);
      });
  }

  ngOnDestroy(): void {
    this.disposeOverlay();
  }

  private fetchRows$(): Observable<unknown[] | null> {
    if (this.item?.query == null || this.item?.options == null) {
      // sem query: renderiza as options como estão (data estática autoral é válida)
      return of(null);
    }

    const executeQuery = this.dvs.applyFilters(this.item.query, this.formFilter);
    this.loading = true;

    return this.dvs.loadData(executeQuery).pipe(
      map(data => (data['SQL'] ?? []) as unknown[]),
      catchError(() => {
        this.message.error(`Falha ao carregar os dados do gráfico "${this.item?.title ?? this.name}".`);
        return of(null);
      }),
      finalize(() => {
        this.loading = false;
      })
    );
  }

  /** deriva as options de render: persistido + data runtime + theme dos tokens */
  private rebuildChartOptions(): void {
    if (this.item?.options == null) {
      this.chartOptions = null;
      return;
    }
    const derived: AgChartOptions = {
      ...this.item.options,
      theme: buildChartTheme(this.el.nativeElement),
    };
    if (this.lastRows != null) {
      (derived as { data?: unknown[] }).data = this.lastRows;
    }
    this.chartOptions = derived;
  }

  excluir(): void {
    this.deleting.emit({ tabName: this.tabName, name: this.name, type: DashBoardTypeEnum.chart });
  }

  openConfiguration(): void {
    const modalRef = openGadgetModal(this.modal, this.vcr, ChartCardConfigurationComponent, {
      item: this.item,
      name: this.name,
      tabName: this.tabName,
      formFilter: this.formFilter,
      idDashBoard: this.dashboard.iddashboard,
      filters: this.dashboard.filter != null ? this.dashboard.filter.map(d => [d.nome]) : null,
      type: DashBoardTypeEnum.chart
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value == null) {
          return;
        }
        this.item = JSON.parse(JSON.stringify(value)) as ChartsInterface;
        this.itemChange.emit(this.item);
        this.refreshSeconds$.next(this.item?.refresh ?? null);
        this.lastRows = null;
        this.rebuildChartOptions();
        this.manualReload$.next();
      });
  }

  // ─────────────────── expandir (overlay) ───────────────────

  openOverlay(): void {
    if (!this.expand) {
      this.expand = true;

      const positionStrategy = this.overlay.position()
        .global()
        .top('2vh')
        .left('2vw');

      this.overlayRef = this.overlay.create({
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-dark-backdrop',
        positionStrategy: positionStrategy,
        width: '96vw',
        height: '96vh'
      });

      const componentPortal = new TemplatePortal(this.content, this.vcr);
      this.overlayRef.attach(componentPortal);

      this.overlayRef.backdropClick()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.disposeOverlay());

    } else {
      this.disposeOverlay();
    }
  }

  private disposeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.expand = false;
  }
}
